'use client';

import { useEffect } from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';
import { ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ApplyJobForm from '../apply-job-form';
import { useState } from 'react';

export default function SalesManagerPosition() {
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className='bg-white dark:bg-gray-950 pt-28 pb-20'>
      <div className='container mx-auto px-4'>
        <Link
          rel='preload'
          href='/careers'
          className='inline-flex items-center text-[#fe2a53] dark:text-[#38f5ef] hover:underline mb-8'
        >
          <ArrowLeft className='w-4 h-4 mr-2' />
          Back to Careers
        </Link>

        <div className='max-w-4xl mx-auto'>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className='mb-8'>
              <h1 className='text-3xl md:text-4xl font-bold text-[#fe2a53] dark:text-[#38f5ef] mb-4 text-center'>
                Sales Manager Real Estate
              </h1>
              <h2 className='text-2xl font-semibold text-[#fe2a53] dark:text-[#38f5ef] mb-8 text-center'>
                JOB DESCRIPTION
              </h2>
            </div>

            <div className='bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-8 md:p-12 mb-8'>
              <h2 className='text-3xl font-bold text-gray-900 dark:text-white mb-6'>
                Do you have a passion for real estate and a knack for building
                high-performing teams?
              </h2>

              <p className='text-gray-700 dark:text-gray-300 mb-8'>
                X-Space Real Estate, a leading Dubai agency specializing in
                residential and commercial properties, is searching for a
                charismatic Sales Manager to join our dynamic team. Here, you'll
                bridge the gap between our network of Chinese investors and
                Dubai's fantastic properties, leveraging your bilingual skills
                (English is a must!) to unlock exceptional opportunities.
              </p>

              <div className='text-center mb-12'>
                <h3 className='text-2xl font-semibold text-gray-900 dark:text-white'>
                  Why X-Space? It's more than just a job. It's a winning
                  formula.
                </h3>
              </div>

              <div className='space-y-8'>
                <section>
                  <h3 className='text-xl font-bold text-[#fe2a53] dark:text-[#38f5ef] mb-4'>
                    Unparalleled Earnings Potential:
                  </h3>
                  <ul className='space-y-4'>
                    <li className='flex items-start'>
                      <div className='w-6 h-6 rounded-full bg-[#fe2a53]/10 dark:bg-[#fe2a53]/20 flex items-center justify-center mr-3 mt-0.5'>
                        <CheckCircle className='w-4 h-4 text-[#fe2a53] dark:text-[#38f5ef]' />
                      </div>
                      <span className='text-gray-700 dark:text-gray-300'>
                        Earn a whopping 80% commission on every deal you close -
                        that's right, you keep the lion's share!
                      </span>
                    </li>
                    <li className='flex items-start'>
                      <div className='w-6 h-6 rounded-full bg-[#fe2a53]/10 dark:bg-[#fe2a53]/20 flex items-center justify-center mr-3 mt-0.5'>
                        <CheckCircle className='w-4 h-4 text-[#fe2a53] dark:text-[#38f5ef]' />
                      </div>
                      <div>
                        <span className='text-gray-700 dark:text-gray-300'>
                          Choose from two competitive compensation plans:
                        </span>
                        <ul className='mt-2 space-y-3 ml-6'>
                          <li className='text-gray-700 dark:text-gray-300'>
                            <span className='font-semibold text-[#fe2a53] dark:text-[#38f5ef]'>
                              Base Salary + Commission (Security & Stability):
                            </span>{' '}
                            Enjoy a guaranteed AED 5,000 base salary with
                            additional team-based commissions and bonuses.
                          </li>
                          <li className='text-gray-700 dark:text-gray-300'>
                            <span className='font-semibold text-[#fe2a53] dark:text-[#38f5ef]'>
                              Full Commission (High Earners):
                            </span>{' '}
                            Take home a sky-high 30% commission on your team's
                            sales, with the flexibility to earn a held
                            commission even if you miss monthly targets (as long
                            as you hit bi-annual or annual goals).
                          </li>
                        </ul>
                      </div>
                    </li>
                  </ul>
                </section>

                <section>
                  <h3 className='text-xl font-bold text-[#fe2a53] dark:text-[#38f5ef] mb-4'>
                    Resources for Success:
                  </h3>
                  <p className='text-gray-700 dark:text-gray-300'>
                    We equip you with the tools you need to win, including
                    access to top listing platforms (Bayt, Dubizzle, Property
                    Finder), powerful data analysis resources, and dedicated
                    administrative and marketing support.
                  </p>
                </section>

                <section>
                  <h3 className='text-xl font-bold text-[#fe2a53] dark:text-[#38f5ef] mb-4'>
                    Become a Dubai Property Guru:
                  </h3>
                  <p className='text-gray-700 dark:text-gray-300'>
                    We equip you with the tools you need to win, including
                    access to top listing platforms (Bayt, Dubizzle, Property
                    Finder), powerful data analysis resources, and dedicated
                    administrative and marketing support.
                  </p>
                </section>

                <section>
                  <h3 className='text-xl font-bold text-[#fe2a53] dark:text-[#38f5ef] mb-4'>
                    Thriving Team Environment:
                  </h3>
                  <p className='text-gray-700 dark:text-gray-300'>
                    Lead and mentor a team of talented sales representatives,
                    fostering a collaborative and results-oriented atmosphere.
                  </p>
                </section>

                <section>
                  <h3 className='text-xl font-bold text-[#fe2a53] dark:text-[#38f5ef] mb-4'>
                    Unlock a World of Opportunity:
                  </h3>
                  <p className='text-gray-700 dark:text-gray-300'>
                    X-Space, a Chinese-owned company with a strong Dubai
                    presence, positions you perfectly to tap into a booming
                    market fueled by Chinese investment.
                  </p>
                </section>

                <section>
                  <h3 className='text-xl font-bold text-[#fe2a53] dark:text-[#38f5ef] mb-4'>
                    Are You the Ideal Candidate?
                  </h3>
                  <ul className='space-y-3'>
                    <li className='flex items-start'>
                      <div className='w-6 h-6 rounded-full bg-[#fe2a53]/10 dark:bg-[#fe2a53]/20 flex items-center justify-center mr-3 mt-0.5'>
                        <CheckCircle className='w-4 h-4 text-[#fe2a53] dark:text-[#38f5ef]' />
                      </div>
                      <span className='text-gray-700 dark:text-gray-300'>
                        You boast a minimum of 2 years' proven success as a
                        Sales Manager in Dubai's real estate industry.
                      </span>
                    </li>
                    <li className='flex items-start'>
                      <div className='w-6 h-6 rounded-full bg-[#fe2a53]/10 dark:bg-[#fe2a53]/20 flex items-center justify-center mr-3 mt-0.5'>
                        <CheckCircle className='w-4 h-4 text-[#fe2a53] dark:text-[#38f5ef]' />
                      </div>
                      <span className='text-gray-700 dark:text-gray-300'>
                        You hold a valid RERA Broker card. (This is a must!)
                      </span>
                    </li>
                    <li className='flex items-start'>
                      <div className='w-6 h-6 rounded-full bg-[#fe2a53]/10 dark:bg-[#fe2a53]/20 flex items-center justify-center mr-3 mt-0.5'>
                        <CheckCircle className='w-4 h-4 text-[#fe2a53] dark:text-[#38f5ef]' />
                      </div>
                      <span className='text-gray-700 dark:text-gray-300'>
                        You're a master communicator and negotiator, adept at
                        building rapport and closing deals.
                      </span>
                    </li>
                    <li className='flex items-start'>
                      <div className='w-6 h-6 rounded-full bg-[#fe2a53]/10 dark:bg-[#fe2a53]/20 flex items-center justify-center mr-3 mt-0.5'>
                        <CheckCircle className='w-4 h-4 text-[#fe2a53] dark:text-[#38f5ef]' />
                      </div>
                      <span className='text-gray-700 dark:text-gray-300'>
                        You can juggle multiple tasks and clients with ease,
                        keeping projects on track.
                      </span>
                    </li>
                    <li className='flex items-start'>
                      <div className='w-6 h-6 rounded-full bg-[#fe2a53]/10 dark:bg-[#fe2a53]/20 flex items-center justify-center mr-3 mt-0.5'>
                        <CheckCircle className='w-4 h-4 text-[#fe2a53] dark:text-[#38f5ef]' />
                      </div>
                      <span className='text-gray-700 dark:text-gray-300'>
                        You possess a deep understanding of the Dubai real
                        estate market, its legalities, and regulations.
                      </span>
                    </li>
                    <li className='flex items-start'>
                      <div className='w-6 h-6 rounded-full bg-[#fe2a53]/10 dark:bg-[#fe2a53]/20 flex items-center justify-center mr-3 mt-0.5'>
                        <CheckCircle className='w-4 h-4 text-[#fe2a53] dark:text-[#38f5ef]' />
                      </div>
                      <span className='text-gray-700 dark:text-gray-300'>
                        You're a tech whiz, proficient in Microsoft Office and
                        CRM software.
                      </span>
                    </li>
                    <li className='flex items-start'>
                      <div className='w-6 h-6 rounded-full bg-[#fe2a53]/10 dark:bg-[#fe2a53]/20 flex items-center justify-center mr-3 mt-0.5'>
                        <CheckCircle className='w-4 h-4 text-[#fe2a53] dark:text-[#38f5ef]' />
                      </div>
                      <span className='text-gray-700 dark:text-gray-300'>
                        (Bonus points!) You hold a Bachelor's degree in
                        Business, Sales, or a related field.
                      </span>
                    </li>
                    <li className='flex items-start'>
                      <div className='w-6 h-6 rounded-full bg-[#fe2a53]/10 dark:bg-[#fe2a53]/20 flex items-center justify-center mr-3 mt-0.5'>
                        <CheckCircle className='w-4 h-4 text-[#fe2a53] dark:text-[#38f5ef]' />
                      </div>
                      <span className='text-gray-700 dark:text-gray-300'>
                        Fluency in Mandarin or Arabic, in addition to English,
                        is a huge plus.
                      </span>
                    </li>
                    <li className='flex items-start'>
                      <div className='w-6 h-6 rounded-full bg-[#fe2a53]/10 dark:bg-[#fe2a53]/20 flex items-center justify-center mr-3 mt-0.5'>
                        <CheckCircle className='w-4 h-4 text-[#fe2a53] dark:text-[#38f5ef]' />
                      </div>
                      <span className='text-gray-700 dark:text-gray-300'>
                        A valid UAE driving license and a car are essential for
                        this on-site role.
                      </span>
                    </li>
                  </ul>
                </section>

                <section>
                  <p className='text-gray-700 dark:text-gray-300 mb-4'>
                    If you're ready to take your career to the next level, we
                    want to hear from you!
                  </p>
                  <h3 className='text-xl font-bold text-[#fe2a53] dark:text-[#38f5ef] mb-4'>
                    Join X-Space Real Estate and:
                  </h3>
                  <ul className='space-y-3'>
                    <li className='flex items-start'>
                      <div className='w-6 h-6 rounded-full bg-[#fe2a53]/10 dark:bg-[#fe2a53]/20 flex items-center justify-center mr-3 mt-0.5'>
                        <CheckCircle className='w-4 h-4 text-[#fe2a53] dark:text-[#38f5ef]' />
                      </div>
                      <span className='text-gray-700 dark:text-gray-300'>
                        Become part of a dynamic and successful team.
                      </span>
                    </li>
                    <li className='flex items-start'>
                      <div className='w-6 h-6 rounded-full bg-[#fe2a53]/10 dark:bg-[#fe2a53]/20 flex items-center justify-center mr-3 mt-0.5'>
                        <CheckCircle className='w-4 h-4 text-[#fe2a53] dark:text-[#38f5ef]' />
                      </div>
                      <span className='text-gray-700 dark:text-gray-300'>
                        Earn top dollar for your hard work.
                      </span>
                    </li>
                    <li className='flex items-start'>
                      <div className='w-6 h-6 rounded-full bg-[#fe2a53]/10 dark:bg-[#fe2a53]/20 flex items-center justify-center mr-3 mt-0.5'>
                        <CheckCircle className='w-4 h-4 text-[#fe2a53] dark:text-[#38f5ef]' />
                      </div>
                      <span className='text-gray-700 dark:text-gray-300'>
                        Enjoy a work-life balance that allows you to thrive.
                      </span>
                    </li>
                  </ul>
                </section>

                <section>
                  <h3 className='text-xl font-bold text-[#fe2a53] dark:text-[#38f5ef] mb-4'>
                    We can't wait to meet you!
                  </h3>
                  <p className='text-gray-700 dark:text-gray-300'>
                    We're also known as Good Reputation Real Estate, so don't be
                    surprised if you hear that name too.
                  </p>
                </section>
              </div>
            </div>

            <div className='text-center'>
              <Button
                onClick={() => setShowForm(true)}
                className='bg-gradient-to-r bg-primarycolor dark:bg-secondarycolor hover:bg-secondarycolor dark:hover:bg-primarycolor text-white hover:text-black dark:hover:text-white px-8 py-6 text-lg rounded-full'
              >
                Apply for this Position
              </Button>
            </div>
          </motion.div>
        </div>
      </div>

      {showForm && (
        <ApplyJobForm
          onClose={() => setShowForm(false)}
          jobTitle='Sales Manager'
        />
      )}
    </div>
  );
}
